<template>
  <Fragment>
    <b-container
      v-if="!userCompanyRoleName && !userCompanyRoleValue"
      class="py-5"
    >
      <BaseAlert variant="danger">You don't have company assigned.</BaseAlert>
    </b-container>

    <BaseDataTable
      v-else
      v-bind="$attrs"
      v-model="modelValue"
      ref="dataTableRef"
      :columns="BDXManagementTableColumns"
      :items="itemsProvider"
      :total="total"
      :initial-page-size="100"
      :page-size-options="[100, 200, 500, 1000]"
      :tbody-tr-class="tableTbodyTrClass"
      selectable
    >
      <template v-slot:header(top)>
        <slot name="header(top)" />
      </template>

      <template v-slot:header(left)>
        <BDXManagementTableFilters v-model="mainFilters" />

        <slot name="header(left)" />
      </template>

      <template v-slot:header(bottom)="slotProps">
        <div class="mt-3 d-flex align-items-center justify-content-between">
          <slot name="header(bottom)" />

          <BDXManagementTableExportButton
            :filters="{ ...slotProps, ...mainFilters }"
            :use-remote-export="1000 < slotProps.total"
          />
        </div>
      </template>

      <template v-slot:head(actions)="slotProps">
        <slot name="head(actions)" v-bind="slotProps" />
      </template>

      <template v-slot:cell(actions)="slotProps">
        <a
          :disabled="!!$props.selected.length"
          :style="{ opacity: !!$props.selected.length ? 0.5 : 1 }"
          role="button"
          title="Invoice Charges"
          @click.prevent="() => $emit('transactions:item', slotProps.item)"
        >
          <BaseIcon icon="cash" tag="i" :font-scale="0.9" />
        </a>

        <a
          :disabled="!!$props.selected.length"
          :style="{ opacity: !!$props.selected.length ? 0.5 : 1, marginLeft: '6px' }"
          role="button"
          @click.prevent="() => $emit('edit:item', slotProps.item)"
        >
          <BaseIcon icon="pencil" tag="i" :font-scale="0.9" />
        </a>

        <slot name="cell(actions)" v-bind="slotProps" />
      </template>

      <template v-slot:cell(bdx_status)="{ item }">
        <GlobalUMRStatusToggle
          :id="item.id"
          :value="item.bdx_status"
          type="bdx"
        />
      </template>

      <template v-slot:cell(billing_status)="{ item }">
        <GlobalUMRStatusToggle
          v-if="isSuperUser"
          :id="item.id"
          :value="item.billing_status"
          :billing-type="item.billing_type || ''"
          :currency-type="item.currency_type || ''"
          type="billing"
        />
      </template>

      <template v-slot:cell(rec_status)="{ item }">
        <GlobalUMRStatusToggle
          v-if="isSuperUser"
          :id="item.id"
          :value="item.rec_status"
          :fcp-status="item.fcp_status"
          type="rec"
        />
      </template>

      <template v-slot:cell(billing_type)="{ item }">
        <GlobalUMRStatusToggle
          v-if="isSuperUser"
          :id="item.id"
          :billing-type="item.billing_type || ''"
          type="billing_type"
        />
      </template>

      <template v-slot:cell(fcp_status)="{ item }">
        <BDXManagementTableFCPToggle
          :id="item.id"
          :start-date="item.fcp_start_date"
          :account-type="item.account_type"
          :account-name="item.account_name"
          :umr="item.umr"
          :value="item.fcp_status"
          :rec-status="item.rec_status"
          @saved:fcp-start-date="handleRefresh"
        />
      </template>

      <template v-slot:cell(created_on)="{ item }">
        <FormattedDate :value="item.created_on" />
      </template>

      <template v-slot:cell(last_report_date)="{ item }">
        <FormattedDate :value="item.last_report_date" />
      </template>

      <template v-slot:cell(tpa)="{ item }">
        <ul v-if="item.tpa" class="mb-0 list-style-none small">
          <li
            :key="uniqueId('bdx-management-table-tpa')"
            class="mb-1 text-wrap"
          >
            {{ item.tpa.TPA }}
          </li>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>

      <template v-slot:cell(underwriter_lead)="{ item }">
        <ul v-if="!!item.underwriter" class="mb-0 list-style-none small">
          <li
            v-for="underwriter in item.underwriter"
            v-if="underwriter.lead"
            :key="uniqueId('bdx-management-table-underwriter-lead')"
            class="mb-1 text-wrap"
          >
            {{ underwriter.rls_underwriter.underwriter }}
          </li>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>

      <template v-slot:cell(underwriter_followers)="{ item }">
        <ul v-if="!!item.underwriter" class="mb-0 list-style-none small">
          <li
            v-for="underwriter in item.underwriter"
            v-if="!underwriter.lead"
            :key="uniqueId('bdx-management-table-underwriter-follower')"
            class="mb-1 text-wrap"
          >
            {{ underwriter.rls_underwriter.underwriter }}
          </li>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>

      <template v-slot:cell(lead_syndicate)="{ item }">
        <ul v-if="!!item.underwriter" class="mb-0 list-style-none small">
          <template v-for="underwriter in item.underwriter">
            <li
              v-for="syndicate in leadSyndicatesOf(underwriter)"
              :key="uniqueId('bdx-management-table-lead-syndicate')"
              class="mb-1 text-wrap"
            >
              {{ syndicate.syndicate_no || '—' }}
              <span class="text-muted">{{ formatSyndicatePct(syndicate) }}</span>
            </li>
          </template>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>

      <template v-slot:cell(follower_syndicates)="{ item }">
        <ul v-if="!!item.underwriter" class="mb-0 list-style-none small">
          <template v-for="underwriter in item.underwriter">
            <li
              v-for="syndicate in followSyndicatesOf(underwriter)"
              :key="uniqueId('bdx-management-table-follower-syndicate')"
              class="mb-1 text-wrap"
            >
              {{ syndicate.syndicate_no || '—' }}
              <span class="text-muted">{{ formatSyndicatePct(syndicate) }}</span>
            </li>
          </template>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>

      <template v-slot:cell(coverholder)="{ item }">
        <ul
          v-if="item.coverholder"
          class="mb-0 list-style-none small"
        >
          <li
            :key="uniqueId('bdx-management-table-coverholder')"
            class="mb-1 text-wrap"
          >
            {{ item.coverholder.coverholder }}
          </li>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>

      <template v-slot:cell(broker)="{ item }">
        <ul v-if="item.broker" class="mb-0 list-style-none small">
          <li
            :key="uniqueId('bdx-management-table-broker')"
            class="mb-1 text-wrap"
          >
            {{ item.broker.broker }}
          </li>
        </ul>
        <span class="text-wrap" v-else></span>
      </template>
    </BaseDataTable>
  </Fragment>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  computed,
  ref,
  nextTick,
  watch,
  onMounted
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { uniqueId } from 'lodash-es'
import { format } from 'date-fns'
import { Fragment } from 'vue-fragment'

import { DATE_FORMAT } from '@constants'
import { useCurrentUser, useUserCompany } from '@composables'
import { LincolnGlobalUMRService } from '@services'

import { BaseAlert, BaseDataTable, BaseIcon } from '@components/base'
import { FormattedDate } from '@components/misc'
import { GlobalUMRStatusToggle } from '@components/global-umr'

import { getBDXManagementTableColumns } from './BDXManagementTable.constants'

import BDXManagementTableExportButton from './BDXManagementTableExportButton.vue'
import BDXManagementTableFilters from './filters/BDXManagementTableFilters.vue'
import BDXManagementTableFCPToggle from './BDXManagementTableFCPToggle.vue'

export default defineComponent({
  name: 'BDXManagementTable',
  components: {
    Fragment,

    BaseAlert,
    BaseDataTable,
    BaseIcon,

    FormattedDate,

    GlobalUMRStatusToggle,

    BDXManagementTableExportButton,
    BDXManagementTableFilters,
    BDXManagementTableFCPToggle
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    selected: {
      type: [Number, Array],
      default: () => []
    }
  },
  emits: ['input', 'edit:item', 'transactions:item'],
  setup(props, { emit }) {
    const { useGetters } = createVuexHelpers()

    const { bdxFilters } = useGetters(['bdxFilters'])

    const { isSuperUser } = useCurrentUser()
    const { roleName: userCompanyRoleName, roleValue: userCompanyRoleValue } =
      useUserCompany()

    const modelValue = computed({
      get: () => props.value,
      set: (newValue) => emit('input', newValue)
    })

    const BDXManagementTableColumns = computed(() =>
        getBDXManagementTableColumns(isSuperUser.value)
    )

    const mainFilters = ref({
      bdx_status: 'All',
      fcp_status: 'All',
      rec_status: 'All',
      payment_status: 'All',
      reserve: 'All',
      claim_status: 'All',
      created_on: []
    })

    const dataTableRef = ref(null)

    const total = ref(0)

    const itemsProvider = async (context) => {
      if (!userCompanyRoleName.value && !userCompanyRoleValue.value) return []

      const { filter, filters, currentPage, perPage, sortBy, sortDesc } =
        context

      const [createdOnStart, createdOnEnd] = mainFilters.value.created_on || []

      const columnFilters = {
        is_superuser: isSuperUser.value,
        bdx_status: [mainFilters.value.bdx_status],
        fcp_status: [mainFilters.value.fcp_status],
        rec_status: [mainFilters.value.rec_status],
        claim_status: mainFilters.value.claim_status,
        payment_status: mainFilters.value.payment_status,
        reserve: mainFilters.value.reserve,
        ...(!!createdOnEnd && {
          created_on_lt: format(createdOnEnd, DATE_FORMAT)
        }),
        ...(!!createdOnStart && {
          created_on_gt: format(createdOnStart, DATE_FORMAT)
        })
      }

      if (!!filters.length) {
        filters.forEach((filter) => {
          if (!!columnFilters[filter.key]) {
            columnFilters[filter.key].push(filter.value)
          } else {
            columnFilters[filter.key] = [filter.value]
          }
        })
      }

      try {
        const companyRole = isSuperUser.value
          ? bdxFilters.value.companyRoleName
          : userCompanyRoleName.value
        const companyRoleValue = isSuperUser.value
          ? bdxFilters.value.companyRoleValue
          : userCompanyRoleValue.value

        const { data } = await LincolnGlobalUMRService.list(
          companyRole,
          companyRoleValue,
          {
            ...columnFilters,
            page: currentPage,
            page_size: perPage,
            search: filter,
            sort: sortBy ? `${sortDesc ? '-' : ''}${sortBy}` : 'created_on'
          }
        )
        const { count, results } = data || {}

        total.value = count

        const items = Object.values(results).flat()

        //Sorts the underwriter followers groups.
        items.forEach((item) => {
          item.underwriter = item.underwriter.sort((ua, ub) => {
            if (ua.lead && ub.lead) {
              return 0
            }
            const uas = ua.rls_underwriter.underwriter
              ? ua.rls_underwriter.underwriter
              : ''
            const ubs = ub.rls_underwriter.underwriter
              ? ub.rls_underwriter.underwriter
              : ''
            return uas.localeCompare(ubs)
          })
          if (sortDesc) {
            item.underwriter = item.underwriter.reverse()
          }
        })

        return items
      } catch (_) {
        total.value = 0

        return []
      }
    }

    const tableTbodyTrClass = (item) => {
      if (!item) return ''

      return item.is_new ? 'text-danger' : ''
    }

    const handleRefresh = async () => {
      await nextTick()

      if (!dataTableRef.value) return

      dataTableRef.value.handleRefresh()
    }

    watch([mainFilters, bdxFilters], handleRefresh, {
      deep: true
    })

    // Lead is a property of the SYNDICATE, not of the underwriter: one market
    // can lead a slip on one syndicate and follow it on another, so the two
    // columns split an underwriter's own list rather than sending all of it to
    // whichever column the underwriter's flag names. A line that has never been
    // told falls back to that flag, which is how every pre-existing row reads.
    const isLeadLine = (underwriter, syndicate) => (
      null === syndicate.lead || undefined === syndicate.lead
        ? !!underwriter.lead
        : !!syndicate.lead
    )

    const leadSyndicatesOf = (underwriter) => (
      (underwriter.syndicates || []).filter((s) => isLeadLine(underwriter, s))
    )

    const followSyndicatesOf = (underwriter) => (
      (underwriter.syndicates || []).filter((s) => !isLeadLine(underwriter, s))
    )

    // Trailing zeros come off, so a plain 40% share reads 40% rather than
    // 40.000000000% - only a share actually entered to 9dp shows all 9.
    // Same rule as Slip Share Service and BDX Edit's own underwriter label.
    // Read-only display only - nothing here writes back.
    const formatSyndicatePct = (syndicate) => {
      const raw = syndicate && syndicate.slip_share_pct
      if (raw === null || raw === undefined || raw === '') return ''
      const n = Number(raw)
      if (!Number.isFinite(n)) return ''
      return `${n.toFixed(9).replace(/\.?0+$/, '')}%`
    }

    onMounted(() => {
      Vue.prototype.$emitter.on('files-uploaded', ({ type, success }) => {
        if (!success || 'bdx' !== type) return

        handleRefresh()
      })
    })

    return {
      BDXManagementTableColumns,
      isSuperUser,

      uniqueId,
      format,
      formatSyndicatePct,
      leadSyndicatesOf,
      followSyndicatesOf,

      userCompanyRoleName,
      userCompanyRoleValue,

      modelValue,

      mainFilters,

      dataTableRef,
      handleRefresh,

      total,

      itemsProvider,

      tableTbodyTrClass
    }
  }
})
</script>

<style scoped>
>>> .filters {
  column-gap: 10px;
}

>>> .filters + div {
  margin-top: auto;
}

>>> .filters + div .base-data-table-search {
  max-width: 420px;
}

>>> .b-table-sticky-column > div {
  min-width: 95px;
  margin: 0 !important;
  display: inline-flex;
  align-items: center;
}

>>> th.b-table-sticky-column > div {
  justify-content: flex-end !important;
}

>>> th.b-table-sticky-column {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

>>> th.b-table-sticky-column > div,
>>> td.b-table-sticky-column > div {
  padding-right: 10px !important;
  padding-left: 10px !important;
}

>>> td.b-table-sticky-column > div .custom-checkbox {
  margin-left: auto;
}
>>> .table.b-table {
  table-layout: fixed;
}
>>> .table.b-table colgroup > col:first-child {
  width: 95px !important;
}
>>> .table.b-table tbody > tr > td > div {
  white-space: normal;
}
</style>
